import {
  Select,
  MultiSelect,
  TextField,
  MaterialSymbol,
  Card,
  withStyles,
  TransactionType,
  Tooltip,
  Modal,
} from 'vopay';
import { Button, CircleButton, UtilButton, CardCheckbox, Checkbox, TransactionTypeLabel } from 'UI';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddApproverDialog from './AddApproverDialog';
import styleSheet from './ApproversEdit.module.scss';

const cn = withStyles(styleSheet);

export interface PaymentRailGroup {
  id: string;
  paymentRails: TransactionType[];
  amount: string;
  hasNoLimit: boolean;
}

export interface ApproverData {
  username: string;
  paymentRailGroups: PaymentRailGroup[];
  addToAllSubAccounts: boolean;
}

interface ApproverCardEditProps {
  approver: ApproverData;
  approverIndex: number;
  availableUsers: Array<{ label: string; value: string }>;
  paymentRailOptions: Array<{ value: string; label: string; template?: React.ReactNode }>;
  errors: Record<string, string>;
  onApproverChange: (username: string) => void;
  onPaymentRailsChange: (groupIndex: number, rails: TransactionType[]) => void;
  onAmountChange: (groupIndex: number, amount: string) => void;
  onNoLimitChange: (groupIndex: number, hasNoLimit: boolean) => void;
  onRemove: () => void;
  onAddPaymentRailGroup: () => void;
  onRemovePaymentRailGroup: (groupIndex: number) => void;
  getAvailablePaymentRails: (
    groupIndex: number,
  ) => Array<{ value: TransactionType; label: string; template?: React.ReactNode }>;
  onNewUser: (email: string) => Promise<void>;
  showAccountSelection: boolean;
  onAddToAllSubAccountsChange: (value: boolean) => void;
}

const ApproverCardEdit = ({
  approver,
  approverIndex,
  availableUsers,
  errors,
  onApproverChange,
  onPaymentRailsChange,
  onAmountChange,
  onNoLimitChange,
  onRemove,
  onAddPaymentRailGroup,
  onRemovePaymentRailGroup,
  getAvailablePaymentRails,
  onNewUser,
  showAccountSelection,
  onAddToAllSubAccountsChange,
}: ApproverCardEditProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Roles.Approvers.Edit',
  });
  const [showAddApprover, setShowAddApprover] = useState(false);

  return (
    <>
      <Card className={cn('approverCard')} hoverEffect={false}>
        <CircleButton
          colorScheme="destructive"
          iconFill
          icon="delete"
          onClick={onRemove}
          size="large"
          aria-label={t('removeButton', { defaultValue: 'Remove' })}
          className={cn('deleteApproverButton')}
        />

        <div className={cn('approverContent')}>
          <div className={cn('userSelectSection')}>
            <div className={cn('railGroupRow')}>
              <div className={cn('formElement')}>
                <label className={cn('fieldLabel')}>{t('approverLabel', { defaultValue: 'Approver' })}</label>
                <Select
                  version="2"
                  name={`approver-${approverIndex}`}
                  placeholder={t('selectUserPlaceholder', { defaultValue: 'Select user' })}
                  options={availableUsers}
                  value={approver.username}
                  filterable
                  onChange={(e) => onApproverChange(e.target.value)}
                  errorMsg={errors[`approver-${approverIndex}-username`]}
                  renderDropdownFooter={({ close }) => (
                    <div>
                      <Button
                        size="small"
                        iconLeft="add"
                        style={{ width: '100%' }}
                        onClick={() => {
                          close();
                          setShowAddApprover(true);
                        }}
                      >
                        {t('newApproverButton', { defaultValue: 'New Approver' })}
                      </Button>
                    </div>
                  )}
                />
              </div>
            </div>

            {showAccountSelection && (
              <Checkbox
                name={`addToAllSubAccounts-${approverIndex}`}
                checked={approver.addToAllSubAccounts}
                onChange={(checked) => onAddToAllSubAccountsChange(checked)}
                label={
                  <span style={{ color: '#2D2D2DBF', lineHeight: '22px' }}>
                    {t('addToAllSubAccountsLabel', {
                      defaultValue: 'Add this user to all existing and future Sub-Accounts',
                    })}
                  </span>
                }
              />
            )}
          </div>

          <div className={cn('paymentRailsBox')}>
            <div className={cn('paymentRailGroups')}>
              {approver.paymentRailGroups.map((group, groupIndex) => {
                const availableRails = getAvailablePaymentRails(groupIndex);
                const selectedRails = group.paymentRails;
                const isFirstGroup = groupIndex === 0;

                return (
                  <div key={group.id} className={cn('railGroupRow')}>
                    <div className={cn('paymentRailColumn')}>
                      {!isFirstGroup && (
                        <CircleButton
                          size="large"
                          colorScheme="destructive"
                          iconFill
                          icon="delete"
                          onClick={() => onRemovePaymentRailGroup(groupIndex)}
                          aria-label={t('removeGroupButton', { defaultValue: 'Remove payment rail group' })}
                          className={cn('deleteRailButton')}
                        />
                      )}

                      <div className={cn('formElement')}>
                        <label className={cn('fieldLabel')}>
                          {t('paymentRailsLabel', { defaultValue: 'Payment Rails' })}

                          <Tooltip
                            position="top"
                            information={t('railsTooltip', {
                              defaultValue:
                                'Only payment rails with an active approval workflow are available for selection',
                            })}
                          />
                        </label>
                        <MultiSelect
                          name={`rails-${approverIndex}-${groupIndex}`}
                          placeholder={t('selectPaymentRailPlaceholder', { defaultValue: 'Select Payment Rail' })}
                          renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
                          options={availableRails}
                          value={selectedRails}
                          // Without this the option's `template` (the Interac
                          // wordmark) is ignored and o.label renders instead.
                          renderOption={(o, { Option, SelectedIcon, selected }) => (
                            <Option>
                              {o.template || o.label}

                              {selected && <SelectedIcon />}
                            </Option>
                          )}
                          onChange={(e) => {
                            const value = e.target.value;
                            onPaymentRailsChange(
                              groupIndex,
                              value === '' ? [] : (value.split(',') as TransactionType[]),
                            );
                          }}
                          errorMsg={errors[`approver-${approverIndex}-group-${groupIndex}-rails`]}
                          renderDropdownFooter={() => {
                            const allSelected = availableRails.every((option) => selectedRails.includes(option.value));

                            return (
                              <div className={cn('multiSelectFooter')}>
                                {allSelected ? (
                                  <Button onClick={() => onPaymentRailsChange(groupIndex, [])} size="small">
                                    {t('deselectAll', { defaultValue: 'Deselect All' })}
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={() => {
                                      const newSelected = [...selectedRails];
                                      availableRails.forEach((option) => {
                                        if (!newSelected.includes(option.value)) {
                                          newSelected.push(option.value);
                                        }
                                      });
                                      onPaymentRailsChange(groupIndex, newSelected);
                                    }}
                                    size="small"
                                  >
                                    {t('selectAll', { defaultValue: 'Select All' })}
                                  </Button>
                                )}
                              </div>
                            );
                          }}
                        />

                        {selectedRails.length > 0 && (
                          <div className={cn('selectedRails')}>
                            {selectedRails.map((rail) => (
                              <div key={rail} className={cn('railChip')}>
                                <span className={cn('railChipText')} data-testid="approver-rail-chip">
                                  <TransactionTypeLabel transactionType={rail} />
                                </span>
                                <div
                                  className={cn('railChipRemove')}
                                  onClick={() =>
                                    onPaymentRailsChange(
                                      groupIndex,
                                      selectedRails.filter((r) => r !== rail),
                                    )
                                  }
                                >
                                  <MaterialSymbol icon="close" />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={cn('formElement')}>
                      <label className={cn('fieldLabel')} aria-hidden="true">
                        &nbsp;
                      </label>
                      <CardCheckbox
                        name={`no-limit-${approverIndex}-${groupIndex}`}
                        checked={group.hasNoLimit}
                        labelPosition="right"
                        onChange={(e) => onNoLimitChange(groupIndex, e.target.checked ?? false)}
                        label={t('noLimitLabel', { defaultValue: 'Approver has no limit' })}
                      />
                    </div>

                    <div className={cn('formElement')} style={{ visibility: group.hasNoLimit ? 'hidden' : 'visible' }}>
                      <label className={cn('fieldLabel')}>
                        {t('maximumApprovalAmountLabel', { defaultValue: 'Maximum Approval Amount' })}
                      </label>
                      <TextField
                        variant="outlineShadow"
                        preset="currency"
                        name={`amount-${approverIndex}-${groupIndex}`}
                        placeholder="0.00"
                        value={group.amount}
                        onChange={(e) => onAmountChange(groupIndex, e.target.value)}
                        error={errors[`approver-${approverIndex}-group-${groupIndex}-amount`]}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <UtilButton onClick={onAddPaymentRailGroup} iconLeft="add" size="small">
              {t('addPaymentRailGroupButton', { defaultValue: 'New Maximum Approval Amount' })}
            </UtilButton>
          </div>
        </div>
      </Card>
      {showAddApprover && (
        <Modal isVisible={true} enableBgClick={false}>
          <AddApproverDialog
            onConfirm={async (email) => {
              setShowAddApprover(false);
              if (email) {
                await onNewUser(email);
                onApproverChange(email);
              }
            }}
            onCancel={() => setShowAddApprover(false)}
          />
        </Modal>
      )}
    </>
  );
};

export default ApproverCardEdit;
